import type {
  CreateFormDTO,
  FormDTO,
  UpdateFormDTO,
} from "@/modules/form-center/domain/contracts";
import type {
  FormSubmissionDetailApiResult,
  FormSubmissionsApiListResult,
  FormsListResult,
} from "@/modules/form-center/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const baseUrl = `${config.apiUrl}/forms`;

export const formsApi = {
  async list(
    query: Partial<OffsetPageQueryDTO> = {},
  ): Promise<FormsListResult> {
    const url = new URL(baseUrl);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.offset) url.searchParams.set("offset", String(query.offset));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<FormsListResult>(url.toString());
  },

  async get(id: string): Promise<FormDTO> {
    return fetcher.get<FormDTO>(`${baseUrl}/${id}`);
  },

  async listSubmissions(
    formId: string,
    query: Partial<OffsetPageQueryDTO> = {},
  ): Promise<FormSubmissionsApiListResult> {
    const url = new URL(`${baseUrl}/${formId}/submissions`);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.offset) url.searchParams.set("offset", String(query.offset));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<FormSubmissionsApiListResult>(url.toString());
  },

  async getSubmissionDetail(
    formId: string,
    submissionId: string,
  ): Promise<FormSubmissionDetailApiResult> {
    return fetcher.get<FormSubmissionDetailApiResult>(
      `${baseUrl}/${formId}/submissions/${submissionId}`,
    );
  },

  async create(input: CreateFormDTO) {
    return fetcher.post(`${baseUrl}`, input);
  },

  async update(id: string, input: UpdateFormDTO) {
    return fetcher.put(`${baseUrl}/${id}`, input);
  },

  async remove(id: string) {
    return fetcher.delete(`${baseUrl}/${id}`);
  },

  async getFieldsByVersion(formId: string, versionId: string) {
    return fetcher.get<{ fields: unknown[] }>(
      `${baseUrl}/${formId}/versions/${versionId}/fields`,
    );
  },

  async getVersions(formId: string) {
    return fetcher.get<{ versions: unknown[] }>(
      `${baseUrl}/${formId}/versions`,
    );
  },

  async duplicate(id: string) {
    return fetcher.post<{ id: string }>(`${baseUrl}/${id}/duplicate`);
  },

  async saveVersion(
    formId: string,
    input: {
      version: string;
      changelog?: string | null;
      fields: Array<{
        label: string;
        fieldType: string;
        isRequired: boolean;
        hint?: string | null;
        orderIndex: number;
        options?: unknown;
        settings?: unknown;
      }>;
      isCurrent?: boolean;
    },
  ) {
    return fetcher.post<{ versionId: string }>(
      `${baseUrl}/${formId}/versions`,
      input,
    );
  },

  async getAnalyticsStats(formId: string) {
    return fetcher.get<{
      totalSubmissions: number;
      submissionsChange: number;
      activeUsers: number;
      usersChange: number;
      avgCompletionTime: string;
      completionChange: number;
    }>(`${baseUrl}/${formId}/analytics/stats`);
  },

  async getSubmissionTrends(
    formId: string,
    period: "7d" | "30d" | "90d" | "1y" = "30d",
  ) {
    return fetcher.get<{ trends: Array<{ date: string; value: number }> }>(
      `${baseUrl}/${formId}/analytics/trends?period=${period}`,
    );
  },

  async getCategories() {
    return fetcher.get<{
      categories: Array<{
        id: string;
        name: string;
        description: string | null;
        icon: string | null;
        orderIndex: number;
        isActive: boolean;
      }>;
    }>(`${baseUrl}/categories`);
  },

  async getCategoryLookup(params?: {
    q?: string;
    limit?: number;
    skip?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.q) searchParams.set("q", params.q);
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.skip) searchParams.set("skip", String(params.skip));
    const queryString = searchParams.toString();
    const url = `${baseUrl}/categories/lookup${queryString ? `?${queryString}` : ""}`;
    return fetcher.get<{
      items: Array<{ id: string; name: string }>;
      total: number;
    }>(url);
  },
};
